import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownSection,
  DropdownItem,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LLMModel } from "@webuild-ai/agent-platform/models/components";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useChatStore } from "@/stores/ChatStore";
import { getBestForDescription } from "@/utils/modelDescriptions";
import { isSpecializedWorkflow } from "@/utils/workflowUtils";

import { ModelIcon } from "./ModelIcon";

const ChatModelSelector = ({
  position = "top-end",
}: {
  position?:
    | "bottom-start"
    | "bottom-end"
    | "top-start"
    | "top-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
}) => {
  const { chat } = useChatStore();
  const { getWorkflow, setWorkflow, workflow } = useWorkflowStore();
  const { platformClient } = useWebuildAPIClient();
  const { t } = useTranslation();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!platformClient) return;
      const availableModels = await platformClient.models.list();
      // Sort models by providerId: openai, anthropic, google
      const providerOrder = ["openai", "anthropic", "google"];

      availableModels.sort((a: LLMModel, b: LLMModel) => {
        const aIndex = providerOrder.indexOf(a.providerId);
        const bIndex = providerOrder.indexOf(b.providerId);

        // If both are in the list, sort by their order
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        // If only a is in the list, it comes first
        if (aIndex !== -1) return -1;
        // If only b is in the list, it comes first
        if (bIndex !== -1) return 1;

        // Otherwise, keep original order
        return 0;
      });

      setModels(availableModels);

      // Set initial selected model
      const workflow = getWorkflow();

      if (!workflow?.agents) {
        setSelectedModel(availableModels[0]);

        return;
      }

      const agentWithModel = Object.values(workflow.agents).find(
        (agent) => agent.parameters?.model,
      );

      if (!agentWithModel) {
        setSelectedModel(availableModels[0]);

        return;
      }

      const model = availableModels.find((m: LLMModel) => m.id === agentWithModel.parameters.model);

      setSelectedModel(model || availableModels[0]);
    };

    fetchModels();
  }, [platformClient, getWorkflow]);

  useEffect(() => {
    if (!models.length) return;

    if (chat?.modelUsed) {
      const modelFromHistory = models.find((m: LLMModel) => m.id === chat.modelUsed);

      if (modelFromHistory) {
        setSelectedModel(modelFromHistory);

        return;
      }
    }

    const workflow = getWorkflow();

    if (!workflow?.agents) {
      setSelectedModel(models[0]);

      return;
    }

    const agentWithModel = Object.values(workflow.agents).find((agent) => agent.parameters?.model);

    if (!agentWithModel) {
      setSelectedModel(models[0]);

      return;
    }

    const model = models.find((m: LLMModel) => m.id === agentWithModel.parameters.model);

    setSelectedModel(model || models[0]);
  }, [workflow, models, chat, getWorkflow]);

  const setWorkflowModel = (model: LLMModel) => {
    /**/
    const workflow = getWorkflow();

    if (!workflow?.agents) return;

    const updatedWorkflow = {
      ...workflow,
      agents: Object.fromEntries(
        Object.entries(workflow.agents).map(([id, agent]) => [
          id,
          {
            ...agent,
            parameters: {
              ...agent.parameters,
              model: model.id,
            },
          },
        ]),
      ),
    };

    setWorkflow(updatedWorkflow);
    setSelectedModel(model);
  };

  // Determine disable reasons
  const hasMessages = chat?.messages && chat?.messages.length > 0;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isDisabled = isSpecialized || hasMessages;

  // Determine tooltip message for the disabled state
  const tooltipMessage = isSpecialized
    ? t("disabled-model-workflow")
    : hasMessages
      ? t("disabled-model-selector")
      : "";

  return (
    <div className="flex items-center gap-2">
      {selectedModel && !isDisabled ? (
        // Enabled branch: Tooltip on hover shows model description; Dropdown opens on click
        <Dropdown data-id="model-selector-dropdown" placement={position}>
          <Tooltip>
            <TooltipTrigger>
              <DropdownTrigger
                className="inline-flex items-center justify-center gap-2 px-3 h-8 text-sm shrink-0 min-w-fit max-w-25 md:flex-1 bg-default-100 hover:bg-default-200 border border-default-300 rounded-full focus-visible:outline-default-400 focus-visible:ring-0"
                data-id="model-selector-button"
                isDisabled={chat?.messages && chat?.messages.length > 0}
              >
                <ModelIcon
                  alt={selectedModel.name}
                  className="text-default-400"
                  height={20}
                  icon={
                    selectedModel.icon.match(/\.(png|jpe?g|svg)$/)
                      ? `/models/${selectedModel.icon}`
                      : selectedModel.icon
                  }
                  width={20}
                />
                <span className="hidden sm:inline">{selectedModel.name}</span>
                <Icon
                  className="text-default-400"
                  height={20}
                  icon="solar:alt-arrow-down-linear"
                  width={20}
                />
              </DropdownTrigger>
            </TooltipTrigger>
            <TooltipContent showArrow className="max-w-70" placement="top">
              {getBestForDescription(selectedModel.name, t)}
            </TooltipContent>
          </Tooltip>
          <DropdownPopover>
            <DropdownMenu
              aria-label={t("Model selector menu")}
              className="px-0 py-4"
              data-id="model-selector-menu"
              style={{ maxHeight: "500px", overflowY: "auto" }}
              variant="ghost"
            >
              <DropdownSection
                className="text-tiny px-2.5"
                data-id="model-selector-section"
                title={t("Model")}
              >
                {models.map((model) => (
                  <DropdownItem
                    key={model.id}
                    className="text-default-500 data-hovered:text-default-500"
                    data-id={`model-item-${model.id}`}
                    textValue={model.name}
                    onPress={() => setWorkflowModel(model)}
                  >
                    <div className="flex items-center gap-2">
                      <ModelIcon
                        alt={model.name}
                        className="text-default-400 shrink-0"
                        height={24}
                        icon={
                          model.icon.match(/\.(png|jpe?g|svg)$/)
                            ? `/models/${model.icon}`
                            : model.icon
                        }
                        width={24}
                      />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span>{model.name}</span>
                        <span className="text-tiny text-default-400">{model.description}</span>
                      </div>
                      {model.id === selectedModel?.id && (
                        <Icon
                          className="text-default-foreground shrink-0"
                          height={24}
                          icon="solar:check-circle-bold"
                          width={24}
                        />
                      )}
                    </div>
                  </DropdownItem>
                ))}
              </DropdownSection>
            </DropdownMenu>
          </DropdownPopover>
        </Dropdown>
      ) : (
        // Disabled branch: Tooltip shows the reason the selector is locked
        <Tooltip>
          <TooltipTrigger>
            <Button
              disabled
              className="shrink-0 min-w-fit max-w-25 md:flex-1 border! border-default-300! rounded-full disabled:bg-default-100 disabled:text-default-400 disabled:cursor-not-allowed"
              data-id="model-selector-button"
              size="sm"
              variant="ghost"
            >
              <ModelIcon
                alt={selectedModel?.name}
                className="text-default-400"
                height={20}
                icon={
                  selectedModel?.icon.match(/\.(png|jpe?g|svg)$/)
                    ? `/models/${selectedModel?.icon}`
                    : (selectedModel?.icon as string)
                }
                width={20}
              />
              <span className="hidden sm:inline">{selectedModel?.name}</span>
              <Icon
                className="text-default-400"
                height={20}
                icon="solar:alt-arrow-down-linear"
                width={20}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent showArrow className="max-w-70" placement="top">
            {tooltipMessage}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export { ChatModelSelector };
